/**
 * Portal Permission Hook
 *
 * Provides granular permission checks for portal users based on their
 * organization role. Replaces binary isPortalView checks for action
 * visibility with role-aware permissions.
 *
 * Organization roles: owner > admin > member > viewer
 */

import { useAuth } from './useAuth';

export interface PortalPermissions {
  /** User is an org owner or admin — can trigger write actions in portal. */
  isOrgAdmin: boolean;
  /** Can trigger pattern enrichment (enrich, forecast generation). */
  canEnrich: boolean;
  /** Can generate implication maps for patterns. */
  canGenerateImplications: boolean;
}

/**
 * Returns portal-specific permissions derived from the user's organization role.
 *
 * Only meaningful when `isPortalView` is true — admin users have full access
 * controlled by the admin/portal routing split, not by these permissions.
 */
export function usePortalPermissions(): PortalPermissions {
  const { organizationRole } = useAuth();
  const isOrgAdmin = organizationRole === 'owner' || organizationRole === 'admin';

  return {
    isOrgAdmin,
    canEnrich: isOrgAdmin,
    canGenerateImplications: isOrgAdmin,
  };
}
